import {
  JsonSchemaProperty,
  RowData,
  RowDataErrors,
  RowDataReducerActionPayload,
  RowDataReducerState,
  MixedValueType,
  RowDataValueType,
} from "../../../types";
import { validateRowDataInput } from "../../../pages/LandingPage/validations";
import { ROW_DATA_KEYS } from "../../../pages/LandingPage/constants";

export const addMultiValuedFieldAction = (
  payload: Partial<RowDataReducerActionPayload>,
  state: RowDataReducerState,
  property: JsonSchemaProperty,
): RowDataReducerState => {
  const {
    propertyKey,
    newFieldValue,
    propertySubKey,
    nestedPropertySubKey,
    nestedRowIndex,
    selectedTabSectionKey,
  } = payload;

  if (
    !propertyKey ||
    newFieldValue === undefined ||
    propertySubKey === undefined
  ) {
    return state;
  }

  if (nestedRowIndex !== undefined && nestedPropertySubKey) {
    if (selectedTabSectionKey) {
      return addTabbedNestedMultiValuedField(
        propertyKey,
        newFieldValue,
        propertySubKey,
        property,
        nestedPropertySubKey,
        nestedRowIndex,
        selectedTabSectionKey,
        state,
      );
    }

    return addNestedMultiValuedField(
      propertyKey,
      newFieldValue,
      propertySubKey,
      property,
      nestedPropertySubKey,
      nestedRowIndex,
      state,
    );
  }

  if (selectedTabSectionKey) {
    return addTabbedMultiValuedField(
      propertyKey,
      newFieldValue,
      propertySubKey,
      property,
      selectedTabSectionKey,
      state,
    );
  }

  return addMultiValuedField(
    propertyKey,
    newFieldValue,
    propertySubKey,
    property,
    state,
  );
};

const addNestedMultiValuedField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  propertySubKey: string,
  property: JsonSchemaProperty,
  nestedPropertySubKey: string,
  nestedRowIndex: number,
  state: RowDataReducerState,
): RowDataReducerState => {
  const parentArray = (state.rowData[propertyKey] as RowData[]) || [];
  const currentRow = (parentArray[nestedRowIndex] as RowData) || {};
  const isKeyedNestedArray = nestedPropertySubKey !== propertySubKey;
  const containerKey = isKeyedNestedArray
    ? propertySubKey
    : nestedPropertySubKey || propertySubKey;
  const nestedArray = (currentRow[containerKey] as MixedValueType[]) || [];

  const nextValue = isKeyedNestedArray
    ? {
        [nestedPropertySubKey]: newFieldValue,
        [ROW_DATA_KEYS.ACTIVE_STATUS]: "active",
      }
    : newFieldValue;
  const updatedNestedArray = [...nestedArray, nextValue] as MixedValueType[];

  const updatedParentArray = parentArray.map((item, index) =>
    index === nestedRowIndex
      ? ({
          ...(item as RowData),
          [containerKey]: updatedNestedArray,
        } as RowData)
      : item,
  );

  const updatedRowData = {
    ...state.rowData,
    [propertyKey]: updatedParentArray,
  } as RowData;

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      newFieldValue,
      state,
    ),
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const addTabbedNestedMultiValuedField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  propertySubKey: string,
  property: JsonSchemaProperty,
  nestedPropertySubKey: string,
  nestedRowIndex: number,
  selectedTabSectionKey: string,
  state: RowDataReducerState,
): RowDataReducerState => {
  const tabData = (state.rowData[selectedTabSectionKey] as RowData) || {};
  const parentArray = (tabData[propertyKey] as RowData[]) || [];
  const currentRow = (parentArray[nestedRowIndex] as RowData) || {};
  const isKeyedNestedArray = nestedPropertySubKey !== propertySubKey;
  const containerKey = isKeyedNestedArray
    ? propertySubKey
    : nestedPropertySubKey || propertySubKey;
  const nestedArray = (currentRow[containerKey] as MixedValueType[]) || [];

  const nextValue = isKeyedNestedArray
    ? {
        [nestedPropertySubKey]: newFieldValue,
        [ROW_DATA_KEYS.ACTIVE_STATUS]: "active",
      }
    : newFieldValue;
  const updatedNestedArray = [...nestedArray, nextValue] as MixedValueType[];

  const updatedParentArray = parentArray.map((item, index) =>
    index === nestedRowIndex
      ? ({
          ...(item as RowData),
          [containerKey]: updatedNestedArray,
        } as RowData)
      : item,
  );

  const updatedRowData = {
    ...state.rowData,
    [selectedTabSectionKey]: {
      ...tabData,
      [propertyKey]: updatedParentArray,
    },
  } as RowData;

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      newFieldValue,
      state,
    ),
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const addTabbedMultiValuedField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  propertySubKey: string,
  property: JsonSchemaProperty,
  selectedTabSectionKey: string,
  state: RowDataReducerState,
): RowDataReducerState => {
  const isSimpleArray = propertyKey === propertySubKey;
  const currentArray =
    ((state.rowData[selectedTabSectionKey] as RowData)?.[
      propertyKey
    ] as MixedValueType[]) || [];

  const updatedArray = isSimpleArray
    ? ([...currentArray, newFieldValue] as MixedValueType[])
    : ([
        ...currentArray,
        {
          [propertySubKey]: newFieldValue,
          [ROW_DATA_KEYS.ACTIVE_STATUS]: "active",
        },
      ] as MixedValueType[]);

  const updatedRowData = {
    ...state.rowData,
    [selectedTabSectionKey]: {
      ...(state.rowData[selectedTabSectionKey] as RowData),
      [propertyKey]: updatedArray,
    },
  } as RowData;

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      newFieldValue,
      state,
    ),
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const addMultiValuedField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  propertySubKey: string,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const isSimpleArray = propertyKey === propertySubKey;
  const currentArray = (state.rowData[propertyKey] as MixedValueType[]) || [];

  const updatedArray = isSimpleArray
    ? ([...currentArray, newFieldValue] as MixedValueType[])
    : ([
        ...currentArray,
        {
          [propertySubKey]: newFieldValue,
          [ROW_DATA_KEYS.ACTIVE_STATUS]: "active",
        },
      ] as MixedValueType[]);

  const updatedRowData = {
    ...state.rowData,
    [propertyKey]: updatedArray,
  } as RowData;

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      newFieldValue,
      state,
    ),
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};
